/* The app's one 32px avatar: the photograph where a person has one, their two letters where they
 * do not.
 *
 * Promoted out of `pages/client-roster/ui/RosterTable.module.css` on 3 September 2026. KANON
 * `Programy v2 - D3 (0.8)` draws the same disc in the plan library's client-plan rows, and the two
 * are one control or they drift the way the search field and the sort control already did once.
 *
 * The two canons DO disagree about the monogram's four cosmetic declarations, measured off both
 * rendered specimens: `klienci v2` writes a `--bg-sunken` ground at 10.5px / 700 / .03em, `Programy
 * v2` writes `--bg-chip` at 9.5px / 800 / .02em under a row that is four fifths the size. `.library`
 * is that second reading and nothing else — the box, the diameter, the hairline and the photograph
 * branch are shared, and a surface that restyles this through `className` is doing the thing the
 * next paragraph forbids.
 *
 * ── The module owns the whole geometry, and a surface may state ONE number ───────────────────
 * `photoBox.module.css` beside this file states the opposite split, and the difference is not an
 * inconsistency: `photoBox` is a TREATMENT applied to boxes of every size, so the surface has to
 * say how big its own box is. An avatar has one size in this app and 32px is it — declared here
 * once, as the fallback of `--sz-avatar`, so that nothing is declared on both sides and a
 * duplicated declaration cannot be settled by chunk order, which differs between the dev server
 * and the build.
 *
 * **The one thing a surface may say is the DIAMETER, through `--sz-avatar` on an ancestor.** It is
 * the same seam `shared/ui/bodyModel/BodySilhouette.module.css` opens with `--h-silhouette`, and it
 * exists because the plan library's client-plan row grew a fifth taller on 4 September 2026 (owner
 * decision D28) and a 32px disc in an 86px row reads as a short row in a tall box. It carries the
 * DIAMETER and nothing else: the ground, the hairline, the monogram and the photograph's fit are
 * still this module's, in both branches, which is what keeps one control from becoming two.
 *
 * A surface's `className` reaches the element for its PLACE in a row (margins, `align-self`,
 * `order`) and for nothing else, exactly as `SearchField` states it. A third diameter gets a
 * variant on the day it exists, not before.
 *
 * The fit and the neutral ground under the photograph are `photoBox`'s `.photo`, applied as a
 * second class in the TSX — see `shared/AGENTS.md`, "A photo holds its own box". */

.chip {
  display: grid;
  flex: none;
  place-items: center;
  width: var(--sz-avatar, 32px);
  height: var(--sz-avatar, 32px);
  overflow: hidden;
  font-size: var(--fs-eyebrow);
  font-weight: var(--fw-bold);
  line-height: var(--lh-none);
  letter-spacing: var(--ls-badge);
  color: var(--text-2);
  background: var(--bg-sunken);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--r-pill);
}

/* KANON Programy v2's reading of the same disc. Four declarations, no geometry: a variant that moved
 * the box would be a second control wearing this one's name. */
.library {
  font-size: var(--fs-micro-label);
  font-weight: var(--fw-extrabold);
  letter-spacing: var(--ls-nav);
  background: var(--bg-chip);
}

/* The video analysis's person mark, in the clip player and the Forma dock: the chip ground, the
 * text ink at 700 with no tracking, and no ring. `.clipSmall` is its 16px reading beside a name. */
.clip {
  font-size: var(--fs-caption);
  font-weight: var(--fw-bold);
  line-height: inherit;
  letter-spacing: normal;
  color: var(--text);
  background: var(--bg-chip);
  border-style: none;
}

.clipSmall {
  font-size: var(--fs-viz-rest);
}

/* An unclaimed invitation: no fill and a dashed edge, the canon's own way of drawing a person who
 * has not arrived yet. It is the ROSTER's state — nobody has claimed the address, so there is no
 * photograph for the other branch to draw — and the plan library has no such row. It stays because
 * it is a real state of a real surface, not because a second caller might want it. */
.pending {
  background: transparent;
  border-style: dashed;
}

/* Geometry only; the fit and the ground are `photoBox`'s. The width and height here are what hold
 * the slot before the file is fetched, and they read the same `--sz-avatar` the monogram does — a
 * photograph and a monogram of two different diameters in one column is the defect one seam avoids. */
.photo {
  flex: none;
  width: var(--sz-avatar, 32px);
  height: var(--sz-avatar, 32px);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--r-pill);
}

/* Doubled so the chip ground outranks `photoBox`'s whatever order the two sheets load in. */
.photo.clipPhoto {
  background: var(--bg-chip);
  border-style: none;
}
